import React from 'react';
import { Helmet } from 'react-helmet-async';
import SeoHead from '../../../shared/seo/SeoHead';
import { BeadedPatchesPageSeo } from '../seo/BeadedPatchesPageSeo';
import '../../pompom/styles/pompoms-page.css';

const FAQ_ICON = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-2h2zm0-4h-2V7h2z" />
    </svg>
);

const TOGGLE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const PRODUCT_TYPES = [
    {
        emoji: '💎',
        name: 'Floral Beaded Patches',
        desc: 'Intricate hand-embroidered floral motifs with seed beads, sequins, and zari thread. Ideal for bridal lehengas, sarees, kurtas, and luxury fashion accessories. Each petal individually stitched by skilled artisans.',
        tags: ['Bridal Range', 'Seed Beads', 'Zari Thread', 'Best Seller'],
    },
    {
        emoji: '✨',
        name: 'Geometric Beaded Patches',
        desc: 'Precision-designed geometric patterns — chevrons, hexagons, diamond grids — using uniform glass beads and metallic sequins. Popular for contemporary fashion, bags, shoes, and home textile accents.',
        tags: ['Glass Beads', 'Metallic Sequins', 'Contemporary', 'Custom Size'],
    },
    {
        emoji: '🦋',
        name: 'Motif & Animal Patches',
        desc: 'Detailed figurative patches — butterflies, peacocks, elephants, and birds — handcrafted with multi-colour seed beads and bugle beads. Statement pieces for jacket backs, denim, and ethnic wear.',
        tags: ['Multi-Colour', 'Bugle Beads', 'Statement Patch', 'Figurative'],
    },
    {
        emoji: '🪡',
        name: 'Beaded Border Patches',
        desc: 'Strip and border patches designed for necklines, cuffs, hems, and dupatta edges. Available in straight, curved, and scalloped forms — fully customisable in width, length, and bead colour.',
        tags: ['Neckline Ready', 'Hem Trim', 'Custom Length', 'Dupatta Edge'],
    },
    {
        emoji: '🌟',
        name: 'Sequin & Bead Combo Patches',
        desc: 'Mixed-texture patches combining flat sequins, cupped sequins, seed beads, and bugle beads for a multi-dimensional shimmer effect. Favoured by fashion designers for festive and resort collections.',
        tags: ['Multi-Texture', 'Festive', 'Resort Wear', 'Designer Range'],
    },
    {
        emoji: '🎨',
        name: 'Custom Logo & Text Patches',
        desc: 'Bespoke beaded patches crafted from your artwork, logo, or text — used for brand embellishments, monograms, wedding favours, and premium corporate gifting. Minimum 20 pieces per design.',
        tags: ['Custom Design', 'Logo Patch', 'Monogram', 'Corporate Gift'],
    },
];

const SPECS = [
    { icon: '📏', label: 'Available Sizes', value: '3 cm – 50 cm (custom sizes on request)' },
    { icon: '🎨', label: 'Bead Colours', value: '150+ seed bead colours + custom Pantone matching' },
    { icon: '💎', label: 'Materials', value: 'Seed beads, bugle beads, sequins, zari, crystal, metal beads' },
    { icon: '📦', label: 'Wholesale MOQ', value: 'From 50 pieces per design / colour' },
    { icon: '🚢', label: 'Global Shipping', value: 'DHL, FedEx, Airmail — 50+ countries' },
    { icon: '⏱️', label: 'Lead Time', value: '10–30 business days depending on complexity' },
];

const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What are handmade beaded patches?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Handmade beaded patches are decorative embroidered appliqués where individual seed beads, bugle beads, sequins, and zari thread are hand-stitched onto a base fabric (organza, net, or felt) to create intricate patterns. Each bead is sewn individually by skilled artisans. At Arte Craft India, every beaded patch is made entirely by hand in our New Delhi workshop — no machine beading — giving each piece exceptional detail, texture, and density.',
            },
        },
        {
            '@type': 'Question',
            name: 'What types of beaded patches does Arte Craft India make?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Arte Craft India manufactures six main categories of beaded patches: Floral beaded patches (seed beads and zari on bridal motifs), Geometric beaded patches (glass beads in structured patterns), Motif and animal patches (butterflies, peacocks, birds in multi-colour beading), Beaded border patches (for necklines, hems, and dupatta edges), Sequin and bead combo patches (mixed-texture shimmer patches), and Custom logo and text patches (bespoke beaded patches from client artwork). All types are available with custom bead colours, sizes, and base fabrics.',
            },
        },
        {
            '@type': 'Question',
            name: 'What materials are used to make beaded patches?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Arte Craft India uses premium beading materials: seed beads (size 11/0 and 15/0 glass beads for fine detail), bugle beads (cylindrical glass beads for linear patterns), flat and cupped sequins (metallic and holographic finishes), zari and metallic thread (gold, silver, antique gold), crystal and AB beads (faceted for maximum light refraction), metal beads and charms (brass and copper plated), on a base of organza, net, felt, or canvas depending on the application.',
            },
        },
        {
            '@type': 'Question',
            name: 'What sizes of beaded patches are available?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Beaded patches are available from 3 cm mini motifs to large 50 cm statement back patches. Standard popular sizes are 5 cm, 8 cm, 10 cm, 12 cm, 15 cm, 20 cm, and 25 cm. Border and strip patches are measured by length and width — widths from 2 cm to 15 cm, lengths from 10 cm up to 150 cm. Custom sizes are produced for wholesale orders. Complex custom designs are quoted based on size, bead density, and estimated stitch count.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I attach a beaded patch to fabric or garment?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Beaded patches are attached using three main methods: (1) Sew-on — the most durable method; hand-stitch around the patch border and through the base fabric using a matching thread and beading needle. (2) Iron-on backing — patches with an iron-on adhesive backing can be heat-pressed onto stable fabrics like denim, cotton, and polyester blends; press for 30–45 seconds at medium heat (do not use steam). (3) Fabric glue — apply fabric adhesive to the patch backing and press firmly; suitable for non-washable decorative applications. We strongly recommend sew-on for beaded patches as heat can damage bead coatings and adhesive bonds weaken with washing.',
            },
        },
        {
            '@type': 'Question',
            name: 'Can I order custom beaded patches from my own design?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — custom design beaded patches are one of Arte Craft India\'s most popular services. To order: (1) Share your design as a high-resolution image, vector file, or sketch. (2) Our artisans produce a bead-colour layout and a physical sample within 10–14 business days. (3) You review and approve the sample. (4) Bulk production begins after written approval. Minimum 20 pieces per custom design. We also offer Pantone-matched bead colour selection for brands requiring exact colour accuracy.',
            },
        },
        {
            '@type': 'Question',
            name: 'What is the minimum order quantity (MOQ) for wholesale beaded patches?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'The wholesale minimum order quantity is 50 pieces per design for standard catalogue designs. For custom designs and bespoke motifs, the MOQ is 20 pieces per design. Wholesale volume price breaks are offered at 50, 100, 250, 500, and 1,000+ units. Private label and custom packaging (polybags, boxes, header cards) are available for orders of 200+ pieces. Contact us for a full wholesale pricing sheet.',
            },
        },
        {
            '@type': 'Question',
            name: 'How long does production take for beaded patches?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Production timelines depend on complexity and quantity: In-stock catalogue patches ship in 3–7 business days. Simple custom designs (up to 100 pieces): 10–14 business days. Complex custom designs (detailed motifs, multi-colour): 15–25 business days. Large wholesale orders (500+ pieces): 4–8 weeks. All lead times begin after sample approval. Rush production is available for simple designs with an additional fee.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I care for garments with beaded patches?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'To protect beaded patches during washing: (1) Hand wash only in cold water (max 30°C) with mild, non-bleaching detergent. (2) Turn the garment inside out before washing. (3) Never machine wash, tumble dry, or wring garments with beaded patches — the agitation can break bead threads and loosen sequins. (4) Do not iron directly over the beaded area; iron the garment from the reverse side only. (5) Dry flat in the shade — do not hang beaded garments as the weight of wet beads can distort the fabric. (6) Store flat or rolled — avoid folding directly across a beaded patch.',
            },
        },
        {
            '@type': 'Question',
            name: 'Are beaded patches suitable for bridal and ethnic Indian wear?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — beaded patches are a defining element of Indian bridal and ethnic fashion. They are used on lehengas, sarees, sherwanis, anarkalis, dupattas, and blouses. Arte Craft India\'s floral beaded patches and zari-combined motifs are specifically designed for bridal applications. Our artisans have experience working with luxury bridal fashion designers, export garment manufacturers, and boutique labels across India and internationally. We supply patches that meet the quality expectations of couture-level bridal wear.',
            },
        },
        {
            '@type': 'Question',
            name: 'Do you ship beaded patches internationally?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — Arte Craft India ships handmade beaded patches to 50+ countries worldwide. All shipments include a tracking number and are carefully packed to prevent bead damage in transit. Shipping options: DHL Express (3–5 business days internationally), FedEx International (4–7 business days), Registered Airmail (10–21 business days, economy). We regularly supply buyers in the USA, UK, Canada, Australia, UAE, Saudi Arabia, France, Germany, and across Europe and South-East Asia. Import duties and taxes are the responsibility of the buyer.',
            },
        },
    ],
};

const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Handmade Beaded Patches — Custom Bead Embroidery Appliqués',
    description:
        'Premium handmade beaded patches manufactured in New Delhi, India. Custom bead embroidery, floral motifs, geometric patches, border strips, and logo patches. 150+ bead colours, custom orders, wholesale MOQ from 50 pieces. Ships to 50+ countries.',
    brand: { '@type': 'Brand', name: 'Arte Craft India' },
    manufacturer: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        telephone: '+91-9818548369',
        email: 'info@artecraftindia.com',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '280/13 Gali No. 29 Molarband Extn.',
            addressLocality: 'Badarpur, New Delhi',
            addressRegion: 'Delhi',
            postalCode: '110044',
            addressCountry: 'IN',
        },
        foundingDate: '2017',
    },
    offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'Arte Craft India' },
    },
    material: 'Seed Beads, Bugle Beads, Sequins, Zari Thread, Crystal Beads, Metal Beads',
    color: '150+ bead colours available',
    countryOfOrigin: 'India',
};

const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.artecraftindia.com' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://www.artecraftindia.com/products' },
        { '@type': 'ListItem', position: 3, name: 'Beaded Patches', item: 'https://www.artecraftindia.com/products/beaded-patches' },
    ],
};

const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Handmade Beaded Patches — Custom Bead Embroidery | Arte Craft India',
    description:
        'Explore Arte Craft India\'s full range of handmade beaded patches — floral motifs, geometric designs, border strips, and custom logo patches. Wholesale & custom orders from New Delhi.',
    url: 'https://www.artecraftindia.com/products/beaded-patches',
    inLanguage: 'en-US',
    publisher: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        logo: {
            '@type': 'ImageObject',
            url: 'https://www.artecraftindia.com/images/logo.jpg',
        },
    },
    speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['#bp-hero-h1', '#bp-about-h2', '#bp-faq-h2'],
    },
};

const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Arte Craft India',
    url: 'https://www.artecraftindia.com',
    logo: 'https://www.artecraftindia.com/images/logo.jpg',
    telephone: '+91-9818548369',
    email: 'info@artecraftindia.com',
    foundingDate: '2017',
    address: {
        '@type': 'PostalAddress',
        streetAddress: '280/13 Gali No. 29 Molarband Extn.',
        addressLocality: 'Badarpur, New Delhi',
        addressRegion: 'Delhi',
        postalCode: '110044',
        addressCountry: 'IN',
    },
    description:
        'Arte Craft India is a direct manufacturer of handmade beaded patches, pom poms, designer tassels, fringes, and textile accessories based in New Delhi, India. Established in 2017, supplying wholesale buyers in 50+ countries.',
};

const BeadedPatchesPage: React.FC = () => {
    return (
        <div className="pp-page">

            <SeoHead {...BeadedPatchesPageSeo} />

            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(productStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(webPageStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(organizationStructuredData)}</script>
            </Helmet>

            {/* ── BREADCRUMB ── */}
            <nav aria-label="Breadcrumb" className="pp-breadcrumb-nav">
                <ol
                    className="pp-breadcrumb"
                    itemScope
                    itemType="https://schema.org/BreadcrumbList"
                >
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/" itemProp="item"><span itemProp="name">Home</span></a>
                        <meta itemProp="position" content="1" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/products" itemProp="item"><span itemProp="name">Products</span></a>
                        <meta itemProp="position" content="2" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span itemProp="name" aria-current="page">Beaded Patches</span>
                        <meta itemProp="position" content="3" />
                    </li>
                </ol>
            </nav>

            {/* ── HERO ── */}
            <section className="pp-hero" aria-labelledby="bp-hero-h1">
                <div className="pp-hero-dots" aria-hidden="true">
                    {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                </div>
                <div className="pp-hero-inner">
                    <div className="pp-hero-badge" aria-hidden="true">
                        Manufacturer Direct · Est. 2017 · New Delhi, India
                    </div>
                    <h1 id="bp-hero-h1">
                        Handmade{' '}
                        <span className="pp-hero-accent">Beaded Patches</span>
                        <br />
                        Custom Embroidery · Bridal &amp; Fashion · Wholesale
                    </h1>
                    <p className="pp-hero-desc speakable-summary">
                        Direct-from-manufacturer handmade beaded patches — floral motifs, geometric designs,
                        border strips &amp; custom logo patches. 150+ bead colours, cruelty-free materials,
                        custom orders and bulk wholesale supplied to 50+ countries since 2017.
                    </p>
                    <div className="pp-hero-stats" role="list" aria-label="Key product highlights">
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">150+</span>
                            <span className="pp-stat-label">Bead Colours</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">3–50cm</span>
                            <span className="pp-stat-label">Sizes</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">50+</span>
                            <span className="pp-stat-label">Countries</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">100%</span>
                            <span className="pp-stat-label">Handmade</span>
                        </div>
                    </div>
                    <div className="pp-hero-cta">
                        <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                            Request a Wholesale Quote
                        </a>
                        <a href="/contact" className="pp-btn pp-btn-outline">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* ── TRUST STRIP ── */}
            <div className="pp-trust-strip" aria-label="Why buy from Arte Craft India">
                <div className="pp-trust-inner">
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🧵</div>
                        <div className="pp-trust-text">
                            <strong>100% Handmade</strong>
                            <span>Every bead stitched by hand</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🎨</div>
                        <div className="pp-trust-text">
                            <strong>Custom Designs</strong>
                            <span>Your artwork, our craftsmanship</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">📦</div>
                        <div className="pp-trust-text">
                            <strong>Wholesale MOQ 50 pcs</strong>
                            <span>Tiered volume pricing</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🌍</div>
                        <div className="pp-trust-text">
                            <strong>Ships Worldwide</strong>
                            <span>DHL, FedEx, Airmail</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">👰</div>
                        <div className="pp-trust-text">
                            <strong>Bridal Specialist</strong>
                            <span>Couture-quality embroidery</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── PRODUCT TYPES ── */}
            <main id="main-content" role="main">
                <div className="pp-wrapper">

                    <div className="pp-section-header">
                        <span className="pp-section-label">Our Range</span>
                        <h2>Beaded Patch Collections</h2>
                        <p className="pp-section-sub">
                            Six distinct beaded patch styles — each crafted for a specific use case,
                            all available in custom designs, bead colours, and wholesale quantities.
                        </p>
                    </div>

                    <div
                        className="pp-types-grid"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        aria-label="Beaded patch product collections"
                    >
                        {PRODUCT_TYPES.map((pt, index) => (
                            <article
                                key={pt.name}
                                className="pp-type-card"
                                itemScope
                                itemProp="itemListElement"
                                itemType="https://schema.org/ListItem"
                            >
                                <meta itemProp="position" content={String(index + 1)} />
                                <span className="pp-type-emoji" aria-hidden="true">{pt.emoji}</span>
                                <h3 itemProp="name">{pt.name}</h3>
                                <p itemProp="description">{pt.desc}</p>
                                <div className="pp-type-tags" aria-label={`${pt.name} specifications`}>
                                    {pt.tags.map(t => <span key={t} className="pp-tag">{t}</span>)}
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* ── ABOUT / SPECIFICATIONS ── */}
                    <section className="pp-about-grid" aria-labelledby="bp-about-h2">
                        <div className="pp-about-prose">
                            <h2 id="bp-about-h2">
                                India's Premier Handmade Beaded Patch Manufacturer
                            </h2>
                            <p>
                                <strong>Arte Craft India</strong> has been producing handmade beaded patches and bead
                                embroidery appliqués from New Delhi since 2017. We are a <strong>direct
                                manufacturer</strong> — not a middleman — which means every beaded patch you receive
                                is crafted in our own workshop by skilled beading artisans, giving you
                                factory-direct quality at the best possible price.
                            </p>
                            <p>
                                Our beaded patches are trusted by <strong>bridal fashion designers, ethnic wear
                                manufacturers, denim brands, accessories labels</strong>, and craft retailers across
                                the USA, UK, Canada, Australia, UAE, Europe, and South-East Asia. We handle
                                everything from single custom motifs to large-volume wholesale contracts
                                of 5,000+ units with private label packaging.
                            </p>
                            <p>
                                Every patch leaves our workshop inspected for <strong>bead density, thread security,
                                colour accuracy, and base fabric integrity</strong>. We use only colourfast,
                                tarnish-resistant beads and premium embroidery thread to ensure our patches
                                maintain their brilliance through years of wear.
                            </p>
                        </div>
                        <div className="pp-specs-list" aria-label="Product specifications">
                            {SPECS.map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── FAQ ── */}
                    <section aria-labelledby="bp-faq-h2" itemScope itemType="https://schema.org/FAQPage">
                        <div className="pp-section-header" style={{ paddingTop: 0 }}>
                            <span className="pp-section-label">Knowledge Base</span>
                            <h2 id="bp-faq-h2">Frequently Asked Questions — Beaded Patches</h2>
                            <p className="pp-section-sub">
                                Everything you need to know before placing your order — from materials and
                                attachment methods to wholesale pricing and international shipping.
                            </p>
                        </div>

                        {/* ── About Beaded Patches ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            About Beaded Patches
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are handmade beaded patches?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Definition</span>
                                        <p itemProp="text">
                                            <strong>Handmade beaded patches</strong> are decorative embroidered appliqués where individual
                                            seed beads, bugle beads, sequins, and zari thread are hand-stitched onto a base fabric —
                                            typically organza, net, or felt — to create intricate patterns and motifs. Each bead is sewn
                                            individually by skilled artisans.
                                        </p>
                                        <p>
                                            At Arte Craft India, every beaded patch is made entirely by hand in our New Delhi workshop —
                                            no machine beading — giving each piece exceptional detail, texture, and density. They are
                                            used on bridal lehengas, sarees, fashion garments, bags, shoes, and home décor.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What types of beaded patches does Arte Craft India make?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Product Range</span>
                                        <p itemProp="text">We manufacture six main categories of beaded patches:</p>
                                        <ul>
                                            <li><strong>Floral Beaded Patches</strong> — Seed bead and zari floral motifs for bridal and ethnic wear</li>
                                            <li><strong>Geometric Beaded Patches</strong> — Glass bead and sequin patterns for contemporary fashion</li>
                                            <li><strong>Motif &amp; Animal Patches</strong> — Butterflies, peacocks, and figurative designs in multi-colour beading</li>
                                            <li><strong>Beaded Border Patches</strong> — Neckline, hem, cuff, and dupatta edge strips in custom lengths</li>
                                            <li><strong>Sequin &amp; Bead Combo Patches</strong> — Mixed-texture shimmer patches for festive and resort collections</li>
                                            <li><strong>Custom Logo &amp; Text Patches</strong> — Bespoke beaded patches from client artwork, logos, or monograms</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What materials are used to make beaded patches?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Materials</span>
                                        <p itemProp="text">We use carefully selected, colourfast materials for every beaded patch:</p>
                                        <ul>
                                            <li><strong>Seed Beads (11/0 &amp; 15/0)</strong> — Fine glass beads for detailed, dense embroidery work</li>
                                            <li><strong>Bugle Beads</strong> — Cylindrical glass beads for linear patterns and geometric fills</li>
                                            <li><strong>Flat &amp; Cupped Sequins</strong> — Metallic and holographic finishes for shimmer and light play</li>
                                            <li><strong>Zari &amp; Metallic Thread</strong> — Gold, silver, and antique gold for traditional embroidery accents</li>
                                            <li><strong>Crystal &amp; AB Beads</strong> — Faceted beads for maximum light refraction on bridal pieces</li>
                                            <li><strong>Metal Beads &amp; Charms</strong> — Brass and copper plated for structured, statement motifs</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What sizes of beaded patches are available?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Sizes</span>
                                        <p itemProp="text">
                                            Beaded patches are available from <strong>3 cm mini motifs</strong> to large
                                            <strong> 50 cm statement back patches</strong>. Standard popular sizes: <strong>5 cm, 8 cm,
                                            10 cm, 12 cm, 15 cm, 20 cm</strong>, and <strong>25 cm</strong>.
                                        </p>
                                        <p>
                                            Border and strip patches are measured by width and length — widths from
                                            <strong> 2 cm to 15 cm</strong>, lengths from <strong>10 cm to 150 cm</strong>.
                                            Custom sizes are produced for wholesale orders. Complex designs are quoted
                                            individually based on size, bead density, and estimated stitch count.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Application & Care ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Application, Care &amp; Durability
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How do I attach a beaded patch to fabric or a garment?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Attachment Guide</span>
                                        <p itemProp="text">There are three main attachment methods for beaded patches:</p>
                                        <ol>
                                            <li>
                                                <strong>Sew-on (most durable)</strong> — Hand-stitch around the patch border and
                                                through the base fabric using a beading needle and matching thread. This is the
                                                recommended method for all washable garments.
                                            </li>
                                            <li>
                                                <strong>Iron-on backing</strong> — Patches with a heat-bond adhesive can be pressed
                                                onto stable fabrics (denim, cotton, polyester blends) at medium heat for 30–45
                                                seconds. Do not use steam. For long-term durability, we recommend sewing around the
                                                edges after ironing.
                                            </li>
                                            <li>
                                                <strong>Fabric glue</strong> — Apply fabric adhesive to the patch backing and press
                                                firmly; suitable for non-washable decorative applications only.
                                            </li>
                                        </ol>
                                        <p>We strongly recommend <strong>sew-on attachment</strong> for beaded patches, as heat
                                        can damage bead coatings and adhesive bonds weaken with repeated washing.</p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How do I care for garments with beaded patches?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Care Instructions</span>
                                        <p itemProp="text">Follow these steps to protect beaded patches during washing and storage:</p>
                                        <ol>
                                            <li><strong>Hand wash only</strong> in cold water (max 30°C) with mild, non-bleach detergent</li>
                                            <li><strong>Turn the garment inside out</strong> before washing</li>
                                            <li><strong>Never machine wash</strong>, tumble dry, or wring — agitation breaks bead threads</li>
                                            <li><strong>Do not iron directly</strong> over beaded areas; iron from the reverse side only</li>
                                            <li><strong>Dry flat in shade</strong> — hanging wet beaded garments distorts the fabric</li>
                                            <li><strong>Store flat or rolled</strong> — avoid folding directly across a beaded patch</li>
                                        </ol>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Are beaded patches suitable for bridal and ethnic Indian wear?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Bridal &amp; Ethnic Fashion</span>
                                        <p itemProp="text">
                                            <strong>Yes — beaded patches are a defining element of Indian bridal and ethnic fashion.</strong>
                                            They are used on lehengas, sarees, sherwanis, anarkalis, dupattas, and blouses.
                                        </p>
                                        <p>
                                            Arte Craft India's floral beaded patches and zari-combined motifs are specifically designed
                                            for bridal applications. Our artisans have experience supplying <strong>couture bridal fashion
                                            designers, export garment manufacturers</strong>, and boutique labels across India and internationally,
                                            meeting the quality expectations of luxury-level bridal wear.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Wholesale & Custom Orders ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Wholesale &amp; Custom Orders
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Can I order custom beaded patches from my own design?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Custom Design</span>
                                        <p itemProp="text">
                                            <strong>Yes — custom design beaded patches are one of our most popular services.</strong> To order:
                                        </p>
                                        <ol>
                                            <li>Share your design as a <strong>high-resolution image, vector file, or sketch</strong></li>
                                            <li>Our artisans produce a <strong>bead-colour layout and physical sample</strong> within 10–14 business days</li>
                                            <li>You review and approve the sample</li>
                                            <li>Bulk production begins after your written approval</li>
                                        </ol>
                                        <p>
                                            Minimum 20 pieces per custom design. We also offer <strong>Pantone-matched bead colour
                                            selection</strong> for brands requiring exact colour accuracy. Custom design patches
                                            are ideal for fashion labels, bridal designers, and corporate gifting.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What is the minimum order quantity (MOQ) for wholesale beaded patches?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">MOQ &amp; Pricing</span>
                                        <p itemProp="text">
                                            For <strong>catalogue standard designs</strong>, the MOQ is <strong>50 pieces per design</strong>.
                                            For <strong>custom designs and bespoke motifs</strong>, the MOQ is <strong>20 pieces per design</strong>.
                                        </p>
                                        <p>
                                            Wholesale volume price breaks at <strong>50, 100, 250, 500, and 1,000+ units</strong>.
                                            Private label and custom packaging (polybags, boxes, header cards) available for
                                            orders of 200+ pieces. Contact us for a full wholesale pricing sheet.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How long does production take for beaded patches?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Lead Times</span>
                                        <p itemProp="text">Production timelines vary by complexity and quantity:</p>
                                        <ul>
                                            <li><strong>In-stock catalogue patches:</strong> Ships in 3–7 business days</li>
                                            <li><strong>Simple custom designs (up to 100 pcs):</strong> 10–14 business days</li>
                                            <li><strong>Complex designs / multi-colour (up to 200 pcs):</strong> 15–25 business days</li>
                                            <li><strong>Large wholesale (500+ pcs):</strong> 4–8 weeks</li>
                                            <li><strong>Rush production:</strong> Available for simple designs — contact us with your deadline</li>
                                        </ul>
                                        <p>All lead times begin <strong>after sample approval</strong>. Please enquire 6–8 weeks
                                        in advance for large bridal season orders.</p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Shipping ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Shipping &amp; After-Sales
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Do you ship beaded patches internationally?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">International Shipping</span>
                                        <p itemProp="text">
                                            <strong>Yes — we ship handmade beaded patches to 50+ countries worldwide.</strong> All orders
                                            include a tracking number and are carefully packed to prevent bead damage in transit.
                                            Available shipping options:
                                        </p>
                                        <ul>
                                            <li><strong>DHL Express</strong> — fastest, typically 3–5 business days internationally</li>
                                            <li><strong>FedEx International</strong> — reliable, 4–7 business days</li>
                                            <li><strong>Registered Airmail</strong> — economy option, 10–21 business days</li>
                                        </ul>
                                        <p>
                                            We regularly supply buyers in the <strong>USA, UK, Canada, Australia, Germany, France,
                                            UAE, Saudi Arabia, Japan</strong>, and across Europe and South-East Asia.
                                            Import duties and taxes are the responsibility of the buyer.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>
                    </section>

                    {/* ── RELATED PRODUCTS ── */}
                    <section className="pp-about-grid" style={{ borderTop: '1px solid var(--pp-border)', paddingTop: 'clamp(40px, 6vw, 72px)' }} aria-labelledby="bp-related-h2">
                        <div className="pp-about-prose">
                            <h2 id="bp-related-h2">Related Products</h2>
                            <p>
                                Arte Craft India's beaded patches are part of a complete range of handcrafted
                                embellishments. Many of our wholesale buyers combine beaded patches with
                                <strong> pom poms</strong>, <strong>designer tassels</strong>, and <strong>beaded necklines</strong>
                                into a single shipment to reduce freight costs.
                            </p>
                            <p>
                                All products are handmade in our New Delhi workshop by the same team of skilled
                                artisans — ensuring consistent quality across your entire order regardless
                                of which product types you combine.
                            </p>
                            <div className="pp-hero-cta" style={{ justifyContent: 'flex-start', marginTop: '20px' }}>
                                <a href="/products/pom-poms" className="pp-btn pp-btn-gold">
                                    View Pom Pom Range
                                </a>
                                <a href="/products" className="pp-btn pp-btn-outline" style={{ color: 'var(--pp-warm-brown)', borderColor: 'var(--pp-border-strong)' }}>
                                    All Products
                                </a>
                            </div>
                        </div>
                        <div className="pp-specs-list" aria-label="Related product categories">
                            {[
                                { icon: '🌺', label: 'Pom Poms', value: 'Faux fur & wool pom poms, 1–25 cm, 200+ colours' },
                                { icon: '🌼', label: 'Tassels', value: 'Pom pom tassels in silk, wool & faux fur' },
                                { icon: '🪡', label: 'Fringes', value: 'Multi-strand fringe trim for garments & textiles' },
                                { icon: '📿', label: 'Beaded Necklines', value: 'Ready-to-stitch beaded neckline panels' },
                                { icon: '✨', label: 'Metallic Patches', value: 'Sequin & metallic bead combo embellishments' },
                            ].map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── CTA ── */}
                    <section className="pp-cta-banner" aria-labelledby="bp-cta-h2">
                        <h2 id="bp-cta-h2">Ready to Order Handmade Beaded Patches?</h2>
                        <p>
                            Get a free wholesale quote within 24 hours. Custom designs, bespoke motifs,
                            bulk orders, and private label packaging available. Trusted by bridal and
                            fashion brands in 50+ countries.
                        </p>
                        <div className="pp-cta-buttons">
                            <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                                Request a Wholesale Quote
                            </a>
                            <a href="/contact" className="pp-btn pp-btn-outline">
                                Ask a Question
                            </a>
                        </div>
                    </section>

                </div>
            </main>
        </div>
    );
};

export default BeadedPatchesPage;
